import { ASC, DESC } from 'NewAngular/constants/core.constants';
/**
 * This function handles common logic to determine the sort column and sort direction in a sortable table
 * when a new column is clicked. The SortColumnType parameter can be any type that represents the possible string names
 * of the columns that can be sorted by.
 * @param defaultSortDirections DefaultSortDirectionConfig object that specifies the default sort direction of each column.
 *                              A typical usage is to set any date columns to sort in DESC order by default.
 * @param currentSortColumn the name of the column currently being sorted by
 * @param newSortColumn the name of the new column that was clicked
 * @param currentSortDirection SortDirection that is currently applied
 * @returns SortColumnAndDirection with the updated sort column and direction
 */
export const getSortColumnAndDirection = function (defaultSortDirections, currentSortColumn, newSortColumn, currentSortDirection) {
    // The new sort direction should be the default sort direction for the newly selected column
    // If no default sort direction is provided for the newly selected column, sort in ascending order
    // "as unknown as string" is needed because of a current typescript limitation.
    // String literal types cannot be used as index signature parameter types: https://github.com/microsoft/TypeScript/issues/5683
    // as a result, the keys must be strings in the defaultSortDirections object, so the newSortColumn must be cast to a string
    let newSortDirection = defaultSortDirections[newSortColumn]
        ? defaultSortDirections[newSortColumn]
        : ASC;
    // Flip the sort direction if the same column was clicked again
    if (newSortColumn === currentSortColumn) {
        newSortDirection = currentSortDirection === ASC ? DESC : ASC;
    }
    return { sortColumn: newSortColumn, sortDirection: newSortDirection };
};
/**
 * This function handles common logic to determine the correct CSS class for a <th> element of a sortable table.
 * The SortColumnType parameter can be any type that represents the possible string names
 * of the columns that can be sorted by.
 * @param currentSortColumn the name of the column currently being sorted by
 * @param sortColumn the name of the column to get the CSS class for
 * @param currentSortDirection SortDirection that is currently applied
 * @returns TableSortDirectionCssClass object that can be directly passed to [ngClass] to apply the correct CSS class
 */
export const getSortDirectionCssClass = function (currentSortColumn, sortColumn, currentSortDirection) {
    return {
        'th-sort-asc': currentSortColumn === sortColumn && currentSortDirection === ASC,
        'th-sort-desc': currentSortColumn === sortColumn && currentSortDirection === DESC,
    };
};
/**
 * Initializes the object that keeps track of which sort direction CSS classes should be applied to which
 * columns in a component with a sortable table
 * @returns SortDirectionCssClasses with sort direction CSS classes initialized to false
 *                                  for all sortable table columns.
 */
export const initializeSortDirectionCssClasses = function (sortColumns) {
    const sortDirectionCssClasses = {};
    // TypeScript does not support enumerating string literal types at run time, so
    // sortable column names must be provided as a list
    sortColumns.forEach((column) => {
        sortDirectionCssClasses[column] = {
            'th-sort-asc': false,
            'th-sort-desc': false,
        };
    });
    return sortDirectionCssClasses;
};
/**
 * Creates a new object with the appropriate sort direction CSS classes for each column
 * of a sortable table. This function should be called whenever the sort column or sort direction changes
 * in a sortable table.
 * @param currentSortDirectionCssClasses SortDirectionCssClasses prior to the sort being changed
 * @param prevSortColumn SortColumnType to disable the sort direction CSS for
 * @param newSortColumn SortColumnType to update the sort direction CSS for
 * @param sortDirection SortDirection that is currently applied
 * @returns SortDirectionCssClasses that match the new sort column and direction
 */
export const getNewSortDirectionCssClasses = function (currentSortDirectionCssClasses, prevSortColumn, newSortColumn, sortDirection) {
    const newSortDirectionCssClasses = { ...currentSortDirectionCssClasses };
    if (typeof prevSortColumn === 'string' && prevSortColumn.length > 0) {
        newSortDirectionCssClasses[prevSortColumn] = {
            'th-sort-asc': false,
            'th-sort-desc': false,
        };
    }
    if (typeof newSortColumn === 'string' && newSortColumn.length > 0) {
        newSortDirectionCssClasses[newSortColumn] = getSortDirectionCssClass(newSortColumn, newSortColumn, sortDirection);
    }
    return newSortDirectionCssClasses;
};
/**
 * Sorts an array of objects that represent items in a table. The objects are sorted according to the
 * sortColumnAndDirection parameter. If the values in the sort column are strings they will be sorted
 * without case sensitivity.
 * @param tableItems
 * @param sortColumnAndDirection
 * @returns TableItemType[] sorted according to the provided sortColumnAndDirection
 */
export const sortTableItems = function (tableItems, sortColumnAndDirection) {
    return tableItems.sort((a, b) => {
        // If the sort direction is descending then the comparison results should be flipped
        const sortDirModifier = sortColumnAndDirection.sortDirection === DESC ? -1 : 1;
        let aSortVal = a[sortColumnAndDirection.sortColumn];
        let bSortVal = b[sortColumnAndDirection.sortColumn];
        // Sort without case sensitivity if values are strings
        if (typeof aSortVal === 'string' && typeof bSortVal === 'string') {
            aSortVal = aSortVal.toLowerCase();
            bSortVal = bSortVal.toLowerCase();
        }
        if (aSortVal < bSortVal) {
            return -1 * sortDirModifier;
        }
        if (bSortVal < aSortVal) {
            return 1 * sortDirModifier;
        }
        return 0;
    });
};
